import React, { useCallback, useEffect, useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";

import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";

import { useSnackbar } from "notistack";

import {
  getKumesler,
  KumesSaveDimensions,
  getKumesCatiTipleri,
  getFanlar,
  getFanBoyutlari,
  getFanGucleri,
  getFanKonumlari,
  saveFan,
  addFan,
  deleteFan,
  getKlapeTipleri,
  SaveKlapeInfo,
  getYakilTipleri,
  getSobaTipleri,
  getSobaKonumlari,
  saveHeatingInfo,
  getPedAdetleri,
  SavePedInfo,
} from "../services/api";

// ─── ÇATI GÖRSELLERİ ─────────────────────────────────────────────────────────
import OpenGable from "../assets/images/OpenGable.jpg";
import BoxGable from "../assets/images/BoxGable.jpg";
import Dormer from "../assets/images/Dormer.jpg";
import MShaped from "../assets/images/MShaped.jpg";
import Hip from "../assets/images/Hip.jpg";
import Jerkinhed from "../assets/images/Jerkinhed.jpg";
import CrossHipped from "../assets/images/CrossHipped.jpg";
import Intersecting from "../assets/images/Intersecting.jpg";
import HipAndValley from "../assets/images/HipandValley.jpg";
import Hexagonal from "../assets/images/Hexagonal.jpg";
import Shed from "../assets/images/Shed.jpg";

const CATI_GORSELLERI = {
  1: { img: OpenGable, label: "Açık Beşik Çatı" },
  2: { img: BoxGable, label: "Kapalı Beşik Çatı" },
  3: { img: Dormer, label: "Çatı Penceresi" },
  4: { img: MShaped, label: "M Tipi Çatı" },
  5: { img: Hip, label: "Kırma Çatı" },
  6: { img: Jerkinhed, label: "Kırma Mansart Çatı" },
  7: { img: CrossHipped, label: "Çapraz Kırma Çatı" },
  8: { img: Intersecting, label: "Kesişen Kırma Çatı" },
  9: { img: HipAndValley, label: "Kırma ve Oluklu Çatı" },
  10: { img: Hexagonal, label: "Altıgen / Kule Çatı" },
  11: { img: Shed, label: "Sundurma Çatı" },
};

// ─── TOKENS ──────────────────────────────────────────────────────────────────
const ORANGE = "#FF7A2B";
const ORANGE_DARK = "#E66A20";
const PAGE_BG = "#F4F6F9";
const WHITE = "#FFFFFF";
const TEXT = "#111827";
const MUTED = "#6B7280";
const BORDER = "#E5E7EB";
const SOFT = "#FAFBFC";

// ─── STYLES ──────────────────────────────────────────────────────────────────
const cardSx = {
  backgroundColor: WHITE,
  border: `1px solid ${BORDER}`,
  borderRadius: "12px",
  boxShadow: "none",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: SOFT,
    "& fieldset": { borderColor: BORDER },
    "&:hover fieldset": { borderColor: "#D1D5DB" },
    "&.Mui-focused fieldset": {
      borderColor: ORANGE,
      borderWidth: "1.5px",
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: 13,
    color: MUTED,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: ORANGE,
  },
};

const tableFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: WHITE,
    fontSize: 13,
    "& fieldset": { borderColor: BORDER },
    "&.Mui-focused fieldset": { borderColor: ORANGE },
  },
};

const saveBtnSx = {
  height: 34,
  px: 2,
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 700,
  fontSize: 13,
  boxShadow: "none",
  backgroundColor: ORANGE,
  "&:hover": { backgroundColor: ORANGE_DARK, boxShadow: "none" },
};

const outlineBtnSx = {
  height: 34,
  px: 2,
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 700,
  fontSize: 13,
  borderColor: ORANGE,
  color: ORANGE,
  "&:hover": {
    borderColor: ORANGE_DARK,
    backgroundColor: "#FFF7F2",
  },
};

const SectionHeader = ({ title, subtitle, action }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    sx={{
      mb: 2,
      pb: 1.25,
      borderBottom: "1px solid #EEF1F4",
      gap: 2,
    }}
  >
    <Box>
      <Typography sx={{ fontSize: 15, fontWeight: 800, color: TEXT }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.25 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {action}
  </Stack>
);

const TinyBadge = ({ label, value }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      px: 1.1,
      py: 0.55,
      borderRadius: "8px",
      backgroundColor: "#F8F9FA",
      border: `1px solid ${BORDER}`,
      whiteSpace: "nowrap",
    }}
  >
    <Typography sx={{ fontSize: 12, color: MUTED }}>{label}</Typography>
    <Typography sx={{ fontSize: 12, color: TEXT, fontWeight: 700 }}>
      {value}
    </Typography>
  </Box>
);

const CatiTipiGorselYatay = ({
  catiTipId,
  catiTipleri,
  value,
  onChange,
}) => {
  const gorsel = CATI_GORSELLERI[catiTipId];

  return (
    <Box
      sx={{
        mt: 2.5,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "380px 1fr" },
        gap: 2,
        alignItems: "stretch",
      }}
    >
      {/* SOL: seçim */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: "12px",
          borderColor: BORDER,
          backgroundColor: "#FCFCFD",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minHeight: 190,
        }}
      >
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: TEXT,
            mb: 1.5,
          }}
        >
          Çatı Tipi Seçimi
        </Typography>

        <TextField
          fullWidth
          select
          size="small"
          label="Çatı Tipi"
          value={value ?? ""}
          onChange={onChange}
          sx={fieldSx}
        >
          {catiTipleri.map((t) => (
            <MenuItem key={t.KUMES_CATI_TIP_ID} value={t.KUMES_CATI_TIP_ID}>
              {t.KUMES_CATI_TIP_ADI}
            </MenuItem>
          ))}
        </TextField>

        <Typography
          sx={{
            fontSize: 12,
            color: MUTED,
            mt: 1.25,
            lineHeight: 1.5,
          }}
        >
          Seçtiğiniz çatı tipi sağ tarafta büyük önizleme olarak gösterilir.
        </Typography>
      </Paper>

      {/* SAĞ: büyük görsel */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: "12px",
          borderColor: BORDER,
          backgroundColor: "#FCFCFD",
          minHeight: 190,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "220px 1fr" },
          gap: 2,
          alignItems: "center",
        }}
      >
        {gorsel ? (
          <>
            <Box
              sx={{
                width: "100%",
                height: { xs: 150, sm: 150 },
                borderRadius: "12px",
                overflow: "hidden",
                border: `1px solid ${BORDER}`,
                backgroundColor: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 1.25,
              }}
            >
              <img
                src={gorsel.img}
                alt={gorsel.label}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: TEXT,
                  lineHeight: 1.2,
                }}
              >
                {gorsel.label}
              </Typography>

              <Typography
                sx={{
                  fontSize: 13,
                  color: MUTED,
                  mt: 1,
                  lineHeight: 1.6,
                  maxWidth: 520,
                }}
              >
                Seçilen çatı tipi için büyük önizleme. Bu alan, çatı tipini daha
                rahat ayırt etmeniz için gösterilir.
              </Typography>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              gridColumn: "1 / -1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ fontSize: 13, color: MUTED }}>
              Çatı tipi seçildiğinde önizleme burada gösterilir.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

const emptyNumber = (v) => (v === null || v === undefined ? "" : v);

const createEmptyFan = (kumesId) => ({
  KUMES_ID: kumesId,
  FAN_NO: "",
  YERDEN_YUKSEKLIK_CM: "",
  BOYUT_ID: "",
  GUC_ID: "",
  KONUM_ID: "",
  isNew: true,
});

const KumesTeknikBilgilerContent = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [hata, setHata] = useState("");
  const [kumesler, setKumesler] = useState([]);
  const [catiTipleri, setCatiTipleri] = useState([]);
  const [fanBoyutlari, setFanBoyutlari] = useState([]);
  const [fanGucleri, setFanGucleri] = useState([]);
  const [fanKonumlari, setFanKonumlari] = useState([]);
  const [klapeTipleri, setKlapeTipleri] = useState([]);
  const [yakitTipleri, setYakitTipleri] = useState([]);
  const [sobaTipleri, setSobaTipleri] = useState([]);
  const [sobaKonumlari, setSobaKonumlari] = useState([]);
  const [pedAdetleri, setPedAdetleri] = useState([]);

  const [dimensionsState, setDimensionsState] = useState({});
  const [fanState, setFanState] = useState({});
  const [klapeState, setKlapeState] = useState({});
  const [heatingState, setHeatingState] = useState({});
  const [pedState, setPedState] = useState({});

  const [savingDimensions, setSavingDimensions] = useState({});
  const [savingFans, setSavingFans] = useState({});
  const [savingKlape, setSavingKlape] = useState({});
  const [savingHeating, setSavingHeating] = useState({});
  const [savingPed, setSavingPed] = useState({});

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setHata("");

      const [
        kumesRes,
        catiTipRes,
        boyutRes,
        gucRes,
        konumRes,
        klapeTipRes,
        yakitTipRes,
        sobaTipRes,
        sobaKonumRes,
        pedAdetRes,
      ] = await Promise.all([
        getKumesler(),
        getKumesCatiTipleri(),
        getFanBoyutlari(),
        getFanGucleri(),
        getFanKonumlari(),
        getKlapeTipleri(),
        getYakilTipleri(),
        getSobaTipleri(),
        getSobaKonumlari(),
        getPedAdetleri(),
      ]);

      const kumesList = kumesRes?.data?.data || [];
      setKumesler(kumesList);
      setCatiTipleri(catiTipRes?.data?.data || []);
      setFanBoyutlari(boyutRes?.data?.data || []);
      setFanGucleri(gucRes?.data?.data || []);
      setFanKonumlari(konumRes?.data?.data || []);
      setKlapeTipleri(klapeTipRes?.data?.data || []);
      setYakitTipleri(yakitTipRes?.data?.data || []);
      setSobaTipleri(sobaTipRes?.data?.data || []);
      setSobaKonumlari(sobaKonumRes?.data?.data || []);
      setPedAdetleri(pedAdetRes?.data?.data || []);

      const fanResults = await Promise.allSettled(
        kumesList.map((k) => getFanlar(k.KUMES_ID))
      );

      const nd = {};
      const nf = {};
      const nk = {};
      const nh = {};
      const np = {};

      kumesList.forEach((kumes, i) => {
        const id = kumes.KUMES_ID;

        nd[id] = {
          KUMES_ID: id,
          KUMES_EN_METRE: emptyNumber(kumes.KUMES_EN_METRE ?? 0),
          KUMES_BOY_METRE: emptyNumber(kumes.KUMES_BOY_METRE ?? 0),
          KUMES_CATI_METRE: emptyNumber(kumes.KUMES_CATI_METRE ?? 0),
          KUMES_SACAK_METRE: emptyNumber(kumes.KUMES_SACAK_METRE ?? 0),
          KUMES_CATI_TIP_ID: kumes.KUMES_CATI_TIP_ID ?? "",
        };

        nk[id] = {
          KUMES_ID: id,
          KLAPE_TIP_ID: kumes.KLAPE_TIP_ID ?? "",
          KLAPE_SAYISI: emptyNumber(kumes.KLAPE_SAYISI ?? 0),
        };

        nh[id] = {
          KUMES_ID: id,
          YAKIT_TIP_ID: kumes.YAKIT_TIP_ID ?? "",
          SOBA_TIP_ID: kumes.SOBA_TIP_ID ?? "",
          SOBA_YER_TIP_ID: kumes.SOBA_YER_TIP_ID ?? "",
        };

        np[id] = {
          KUMES_ID: id,
          PED_ADET_ID: kumes.PED_ADET_ID ?? "",
          PED_EN_CM: emptyNumber(kumes.PED_EN_CM ?? 0),
          PED_BOY_CM: emptyNumber(kumes.PED_BOY_CM ?? 0),
        };

        nf[id] =
          fanResults[i].status === "fulfilled"
            ? fanResults[i].value?.data?.data || []
            : [];
      });

      setDimensionsState(nd);
      setFanState(nf);
      setKlapeState(nk);
      setHeatingState(nh);
      setPedState(np);
    } catch {
      setHata("Kümes teknik bilgileri yüklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toplamKumes = useMemo(() => kumesler.length, [kumesler]);
  const toplamFan = useMemo(
    () => Object.values(fanState).reduce((a, arr) => a + (arr?.length || 0), 0),
    [fanState]
  );

  const handleDimensionChange = (id, f, v) =>
    setDimensionsState((p) => ({ ...p, [id]: { ...p[id], [f]: v } }));

  const handleKlapeChange = (id, f, v) =>
    setKlapeState((p) => ({ ...p, [id]: { ...p[id], [f]: v } }));

  const handleHeatingChange = (id, f, v) =>
    setHeatingState((p) => ({ ...p, [id]: { ...p[id], [f]: v } }));

  const handlePedChange = (id, f, v) =>
    setPedState((p) => ({ ...p, [id]: { ...p[id], [f]: v } }));

  const handleSaveDimensions = async (kumesId) => {
    const p = dimensionsState[kumesId];
    if (!p) return;

    try {
      setSavingDimensions((s) => ({ ...s, [kumesId]: true }));

      await KumesSaveDimensions({
        KUMES_ID: Number(kumesId),
        KUMES_EN_METRE: Number(p.KUMES_EN_METRE || 0),
        KUMES_BOY_METRE: Number(p.KUMES_BOY_METRE || 0),
        KUMES_CATI_METRE: Number(p.KUMES_CATI_METRE || 0),
        KUMES_SACAK_METRE: Number(p.KUMES_SACAK_METRE || 0),
        KUMES_CATI_TIP_ID: Number(p.KUMES_CATI_TIP_ID || 0),
      });

      enqueueSnackbar("Fiziksel bilgiler kaydedildi.", { variant: "success" });
      setKumesler((prev) =>
        prev.map((k) => (k.KUMES_ID === kumesId ? { ...k, ...p } : k))
      );
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.message || "Kayıt sırasında hata oluştu.",
        { variant: "error" }
      );
    } finally {
      setSavingDimensions((s) => ({ ...s, [kumesId]: false }));
    }
  };

  const handleSaveKlape = async (kumesId) => {
    const p = klapeState[kumesId];
    if (!p) return;

    try {
      setSavingKlape((s) => ({ ...s, [kumesId]: true }));

      await SaveKlapeInfo({
        KUMES_ID: Number(kumesId),
        KLAPE_TIP_ID: Number(p.KLAPE_TIP_ID || 0),
        KLAPE_SAYISI: Number(p.KLAPE_SAYISI || 0),
      });

      enqueueSnackbar("Klape bilgileri kaydedildi.", { variant: "success" });
      setKumesler((prev) =>
        prev.map((k) => (k.KUMES_ID === kumesId ? { ...k, ...p } : k))
      );
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.message || "Kayıt sırasında hata oluştu.",
        { variant: "error" }
      );
    } finally {
      setSavingKlape((s) => ({ ...s, [kumesId]: false }));
    }
  };

  const handleSaveHeating = async (kumesId) => {
    const p = heatingState[kumesId];
    if (!p) return;

    try {
      setSavingHeating((s) => ({ ...s, [kumesId]: true }));

      await saveHeatingInfo({
        KUMES_ID: Number(kumesId),
        YAKIT_TIP_ID: Number(p.YAKIT_TIP_ID || 0),
        SOBA_TIP_ID: Number(p.SOBA_TIP_ID || 0),
        SOBA_YER_TIP_ID: Number(p.SOBA_YER_TIP_ID || 0),
      });

      enqueueSnackbar("Isıtma bilgileri kaydedildi.", { variant: "success" });
      setKumesler((prev) =>
        prev.map((k) => (k.KUMES_ID === kumesId ? { ...k, ...p } : k))
      );
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.message || "Kayıt sırasında hata oluştu.",
        { variant: "error" }
      );
    } finally {
      setSavingHeating((s) => ({ ...s, [kumesId]: false }));
    }
  };

  const handleSavePed = async (kumesId) => {
    const p = pedState[kumesId];
    if (!p) return;

    try {
      setSavingPed((s) => ({ ...s, [kumesId]: true }));

      await SavePedInfo({
        KUMES_ID: Number(kumesId),
        PED_ADET_ID: Number(p.PED_ADET_ID || 0),
        PED_EN_CM: Number(p.PED_EN_CM || 0),
        PED_BOY_CM: Number(p.PED_BOY_CM || 0),
      });

      enqueueSnackbar("Ped bilgileri kaydedildi.", { variant: "success" });
      setKumesler((prev) =>
        prev.map((k) => (k.KUMES_ID === kumesId ? { ...k, ...p } : k))
      );
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.message || "Kayıt sırasında hata oluştu.",
        { variant: "error" }
      );
    } finally {
      setSavingPed((s) => ({ ...s, [kumesId]: false }));
    }
  };

  const handleFanFieldChange = (kumesId, index, field, value) => {
    setFanState((prev) => {
      const list = [...(prev[kumesId] || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, [kumesId]: list };
    });
  };

  const handleAddNewFanRow = (kumesId) => {
    setFanState((prev) => ({
      ...prev,
      [kumesId]: [...(prev[kumesId] || []), createEmptyFan(kumesId)],
    }));
  };

  const handleSaveFan = async (kumesId, fan, index) => {
    try {
      setSavingFans((s) => ({ ...s, [`${kumesId}-${index}`]: true }));

      const payload = {
        KUMES_FAN_ID: fan.KUMES_FAN_ID,
        KUMES_ID: Number(kumesId),
        FAN_NO: fan.FAN_NO,
        YERDEN_YUKSEKLIK_CM: Number(fan.YERDEN_YUKSEKLIK_CM || 0),
        BOYUT_ID: Number(fan.BOYUT_ID || 0),
        GUC_ID: Number(fan.GUC_ID || 0),
        KONUM_ID: Number(fan.KONUM_ID || 0),
      };

      if (fan.isNew || !fan.KUMES_FAN_ID) {
        await addFan(payload);
        enqueueSnackbar("Yeni fan eklendi.", { variant: "success" });
      } else {
        await saveFan(payload);
        enqueueSnackbar("Fan güncellendi.", { variant: "success" });
      }

      const refreshed = await getFanlar(kumesId);
      setFanState((prev) => ({
        ...prev,
        [kumesId]: refreshed?.data?.data || [],
      }));
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.message || "Fan kaydı sırasında hata.",
        { variant: "error" }
      );
    } finally {
      setSavingFans((s) => ({ ...s, [`${kumesId}-${index}`]: false }));
    }
  };

  const handleDeleteFan = async (kumesId, fanId) => {
    if (!fanId) {
      setFanState((prev) => ({
        ...prev,
        [kumesId]: (prev[kumesId] || []).filter((f) => f.KUMES_FAN_ID),
      }));
      return;
    }

    try {
      await deleteFan(fanId);
      enqueueSnackbar("Fan silindi.", { variant: "success" });

      const refreshed = await getFanlar(kumesId);
      setFanState((prev) => ({
        ...prev,
        [kumesId]: refreshed?.data?.data || [],
      }));
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || "Fan silinirken hata.", {
        variant: "error",
      });
    }
  };

  const getCatiTipiAdi = (id) =>
    catiTipleri.find((x) => x.KUMES_CATI_TIP_ID === id)?.KUMES_CATI_TIP_ADI || "-";

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
        <CircularProgress sx={{ color: ORANGE }} />
      </Box>
    );
  }

  if (hata) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{hata}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2.5 },
        backgroundColor: PAGE_BG,
        minHeight: "100vh",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={1.5}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: TEXT }}>
            Kümes Teknik Bilgileri
          </Typography>
          <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.25 }}>
            Daha sade görünümle kümes teknik bilgilerini düzenleyin
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <TinyBadge label="Kümes" value={toplamKumes} />
          <TinyBadge label="Fan" value={toplamFan} />
        </Stack>
      </Stack>

      <Stack spacing={1.5}>
        {kumesler.map((kumes, index) => {
          const currentDimensions = dimensionsState[kumes.KUMES_ID] || {};
          const currentFans = fanState[kumes.KUMES_ID] || [];
          const currentKlape = klapeState[kumes.KUMES_ID] || {};
          const currentHeating = heatingState[kumes.KUMES_ID] || {};
          const currentPed = pedState[kumes.KUMES_ID] || {};

          return (
            <Accordion
              key={kumes.KUMES_ID}
              disableGutters
              defaultExpanded={false}
              sx={{
                ...cardSx,
                borderRadius: "12px !important",
                overflow: "hidden",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: ORANGE }} />}
                sx={{
                  px: { xs: 2, md: 2.5 },
                  py: 1.5,
                  minHeight: "unset",
                  "& .MuiAccordionSummary-content": { my: 0 },
                  "&:hover": { backgroundColor: "#FAFAFA" },
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ width: "100%", pr: 1, gap: 2 }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "10px",
                        display: "grid",
                        placeItems: "center",
                        backgroundColor: "#FFF7F2",
                        color: ORANGE,
                        flexShrink: 0,
                      }}
                    >
                      <WarehouseOutlinedIcon fontSize="small" />
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: 15, fontWeight: 800, color: TEXT }}>
                        {kumes.KUMES_ADI || "-"}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.2 }}>
                        {kumes.IL_ADI || "-"} / {kumes.ILCE_ADI || "-"}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={1} sx={{ display: { xs: "none", md: "flex" } }}>
                    <TinyBadge
                      label="Çatı"
                      value={getCatiTipiAdi(currentDimensions.KUMES_CATI_TIP_ID)}
                    />
                    <TinyBadge label="Fan" value={currentFans.length} />
                  </Stack>
                </Stack>
              </AccordionSummary>

              <AccordionDetails
                sx={{
                  p: { xs: 2, md: 2.5 },
                  backgroundColor: PAGE_BG,
                }}
              >
                <Stack spacing={2}>
                  {/* FİZİKSEL BİLGİLER */}
                  <Paper sx={{ ...cardSx, p: 2.5 }}>
                    <SectionHeader
                      title="Fiziksel Bilgiler"
                      subtitle="Kümes ölçüleri ve çatı tipi"
                      action={
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<SaveOutlinedIcon sx={{ fontSize: 14 }} />}
                          onClick={() => handleSaveDimensions(kumes.KUMES_ID)}
                          disabled={!!savingDimensions[kumes.KUMES_ID]}
                          sx={saveBtnSx}
                        >
                          {savingDimensions[kumes.KUMES_ID] ? "Kaydediliyor…" : "Kaydet"}
                        </Button>
                      }
                    />

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "1fr 1fr",
                          lg: "repeat(4, 1fr)",
                        },
                        gap: 2,
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="En (m)"
                        value={currentDimensions.KUMES_EN_METRE ?? ""}
                        onChange={(e) =>
                          handleDimensionChange(kumes.KUMES_ID, "KUMES_EN_METRE", e.target.value)
                        }
                        sx={fieldSx}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Boy (m)"
                        value={currentDimensions.KUMES_BOY_METRE ?? ""}
                        onChange={(e) =>
                          handleDimensionChange(kumes.KUMES_ID, "KUMES_BOY_METRE", e.target.value)
                        }
                        sx={fieldSx}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Çatı Yüksekliği (m)"
                        value={currentDimensions.KUMES_CATI_METRE ?? ""}
                        onChange={(e) =>
                          handleDimensionChange(kumes.KUMES_ID, "KUMES_CATI_METRE", e.target.value)
                        }
                        sx={fieldSx}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Saçak Yüksekliği (m)"
                        value={currentDimensions.KUMES_SACAK_METRE ?? ""}
                        onChange={(e) =>
                          handleDimensionChange(kumes.KUMES_ID, "KUMES_SACAK_METRE", e.target.value)
                        }
                        sx={fieldSx}
                      />
                    </Box>

                              <CatiTipiGorselYatay
              catiTipId={currentDimensions.KUMES_CATI_TIP_ID}
              catiTipleri={catiTipleri}
              value={currentDimensions.KUMES_CATI_TIP_ID ?? ""}
              onChange={(e) =>
                handleDimensionChange(
                  kumes.KUMES_ID,
                  "KUMES_CATI_TIP_ID",
                  e.target.value
                )
              }
            />
                  </Paper>

                  {/* FANLAR */}
                  <Paper sx={{ ...cardSx, p: 2.5 }}>
                    <SectionHeader
                      title="Fanlar"
                      subtitle="Fan kayıtlarını düzenleyin"
                      action={
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                          onClick={() => handleAddNewFanRow(kumes.KUMES_ID)}
                          sx={outlineBtnSx}
                        >
                          Yeni Fan
                        </Button>
                      }
                    />

                    <TableContainer
                      sx={{
                        border: `1px solid ${BORDER}`,
                        borderRadius: "10px",
                        overflow: "hidden",
                        backgroundColor: "#fff",
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#F8F9FA" }}>
                            {["Fan No", "Yükseklik", "Boyut", "Güç", "Konum", ""].map((h, i) => (
                              <TableCell
                                key={i}
                                align={i === 5 ? "right" : "left"}
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: MUTED,
                                  borderColor: BORDER,
                                  py: 1.15,
                                }}
                              >
                                {h}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {currentFans.length > 0 ? (
                            currentFans.map((fan, fi) => (
                              <TableRow
                                key={fan.KUMES_FAN_ID || `new-${fi}`}
                                sx={{ "& td": { borderColor: "#F1F3F5", py: 1 } }}
                              >
                                <TableCell sx={{ minWidth: 100 }}>
                                  <TextField
                                    size="small"
                                    value={fan.FAN_NO ?? ""}
                                    onChange={(e) =>
                                      handleFanFieldChange(
                                        kumes.KUMES_ID,
                                        fi,
                                        "FAN_NO",
                                        e.target.value
                                      )
                                    }
                                    sx={tableFieldSx}
                                  />
                                </TableCell>

                                <TableCell sx={{ minWidth: 130 }}>
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={fan.YERDEN_YUKSEKLIK_CM ?? ""}
                                    onChange={(e) =>
                                      handleFanFieldChange(
                                        kumes.KUMES_ID,
                                        fi,
                                        "YERDEN_YUKSEKLIK_CM",
                                        e.target.value
                                      )
                                    }
                                    sx={tableFieldSx}
                                  />
                                </TableCell>

                                <TableCell sx={{ minWidth: 160 }}>
                                  <TextField
                                    size="small"
                                    select
                                    fullWidth
                                    value={fan.BOYUT_ID ?? ""}
                                    onChange={(e) =>
                                      handleFanFieldChange(
                                        kumes.KUMES_ID,
                                        fi,
                                        "BOYUT_ID",
                                        e.target.value
                                      )
                                    }
                                    sx={tableFieldSx}
                                  >
                                    {fanBoyutlari.map((x) => (
                                      <MenuItem key={x.BOYUT_ID} value={x.BOYUT_ID}>
                                        {x.BOYUT_ADI}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </TableCell>

                                <TableCell sx={{ minWidth: 140 }}>
                                  <TextField
                                    size="small"
                                    select
                                    fullWidth
                                    value={fan.GUC_ID ?? ""}
                                    onChange={(e) =>
                                      handleFanFieldChange(
                                        kumes.KUMES_ID,
                                        fi,
                                        "GUC_ID",
                                        e.target.value
                                      )
                                    }
                                    sx={tableFieldSx}
                                  >
                                    {fanGucleri.map((x) => (
                                      <MenuItem key={x.GUC_ID} value={x.GUC_ID}>
                                        {x.GUC_ADI}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </TableCell>

                                <TableCell sx={{ minWidth: 160 }}>
                                  <TextField
                                    size="small"
                                    select
                                    fullWidth
                                    value={fan.KONUM_ID ?? ""}
                                    onChange={(e) =>
                                      handleFanFieldChange(
                                        kumes.KUMES_ID,
                                        fi,
                                        "KONUM_ID",
                                        e.target.value
                                      )
                                    }
                                    sx={tableFieldSx}
                                  >
                                    {fanKonumlari.map((x) => (
                                      <MenuItem key={x.KONUM_ID} value={x.KONUM_ID}>
                                        {x.KONUM_ADI}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </TableCell>

                                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleSaveFan(kumes.KUMES_ID, fan, fi)}
                                    disabled={!!savingFans[`${kumes.KUMES_ID}-${fi}`]}
                                    sx={{
                                      mr: 0.75,
                                      color: ORANGE,
                                      backgroundColor: "#FFF7F2",
                                      borderRadius: "8px",
                                      width: 32,
                                      height: 32,
                                    }}
                                  >
                                    <SaveOutlinedIcon sx={{ fontSize: 16 }} />
                                  </IconButton>

                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteFan(kumes.KUMES_ID, fan.KUMES_FAN_ID)}
                                    sx={{
                                      color: "#E53935",
                                      backgroundColor: "#FFF0F0",
                                      borderRadius: "8px",
                                      width: 32,
                                      height: 32,
                                    }}
                                  >
                                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell
                                colSpan={6}
                                align="center"
                                sx={{ py: 3, color: MUTED, fontSize: 13 }}
                              >
                                Bu kümeye ait fan bulunamadı.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>

                  {/* EKİPMAN BİLGİLERİ */}
                  <Paper sx={{ ...cardSx, p: 2.5 }}>
                    <SectionHeader
                      title="Ekipman Bilgileri"
                      subtitle="Klape, ped ve ısıtma bilgileri"
                    />

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                        gap: 3,
                      }}
                    >
                      {/* KLAPE */}
                      <Box>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mb: 1.5 }}
                        >
                          <Typography sx={{ fontWeight: 800, fontSize: 14, color: TEXT }}>
                            Klape
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<SaveOutlinedIcon sx={{ fontSize: 14 }} />}
                            onClick={() => handleSaveKlape(kumes.KUMES_ID)}
                            disabled={!!savingKlape[kumes.KUMES_ID]}
                            sx={saveBtnSx}
                          >
                            {savingKlape[kumes.KUMES_ID] ? "…" : "Kaydet"}
                          </Button>
                        </Stack>

                        <Stack spacing={1.5}>
                          <TextField
                            fullWidth
                            select
                            size="small"
                            label="Klape Tipi"
                            value={currentKlape.KLAPE_TIP_ID ?? ""}
                            onChange={(e) =>
                              handleKlapeChange(
                                kumes.KUMES_ID,
                                "KLAPE_TIP_ID",
                                e.target.value
                              )
                            }
                            sx={fieldSx}
                          >
                            {klapeTipleri.map((t) => (
                              <MenuItem key={t.KLAPE_TIP_ID} value={t.KLAPE_TIP_ID}>
                                {t.KLAPE_TIP_ADI}
                              </MenuItem>
                            ))}
                          </TextField>

                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Klape Sayısı"
                            value={currentKlape.KLAPE_SAYISI ?? ""}
                            onChange={(e) =>
                              handleKlapeChange(
                                kumes.KUMES_ID,
                                "KLAPE_SAYISI",
                                e.target.value
                              )
                            }
                            sx={fieldSx}
                          />
                        </Stack>
                      </Box>

                      {/* PED */}
                      <Box>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mb: 1.5 }}
                        >
                          <Typography sx={{ fontWeight: 800, fontSize: 14, color: TEXT }}>
                            Ped Sistemi
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<SaveOutlinedIcon sx={{ fontSize: 14 }} />}
                            onClick={() => handleSavePed(kumes.KUMES_ID)}
                            disabled={!!savingPed[kumes.KUMES_ID]}
                            sx={saveBtnSx}
                          >
                            {savingPed[kumes.KUMES_ID] ? "…" : "Kaydet"}
                          </Button>
                        </Stack>

                        <Stack spacing={1.5}>
                          <TextField
                            fullWidth
                            select
                            size="small"
                            label="Ped Adedi"
                            value={currentPed.PED_ADET_ID ?? ""}
                            onChange={(e) =>
                              handlePedChange(
                                kumes.KUMES_ID,
                                "PED_ADET_ID",
                                e.target.value
                              )
                            }
                            sx={fieldSx}
                          >
                            {pedAdetleri.map((x) => (
                              <MenuItem key={x.PED_ADET_ID} value={x.PED_ADET_ID}>
                                {x.PED_ADET_ADI}
                              </MenuItem>
                            ))}
                          </TextField>

                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Ped En (cm)"
                            value={currentPed.PED_EN_CM ?? ""}
                            onChange={(e) =>
                              handlePedChange(
                                kumes.KUMES_ID,
                                "PED_EN_CM",
                                e.target.value
                              )
                            }
                            sx={fieldSx}
                          />

                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Ped Boy (cm)"
                            value={currentPed.PED_BOY_CM ?? ""}
                            onChange={(e) =>
                              handlePedChange(
                                kumes.KUMES_ID,
                                "PED_BOY_CM",
                                e.target.value
                              )
                            }
                            sx={fieldSx}
                          />
                        </Stack>
                      </Box>

                      {/* ISITMA */}
                      <Box>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mb: 1.5 }}
                        >
                          <Typography sx={{ fontWeight: 800, fontSize: 14, color: TEXT }}>
                            Isıtma
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<SaveOutlinedIcon sx={{ fontSize: 14 }} />}
                            onClick={() => handleSaveHeating(kumes.KUMES_ID)}
                            disabled={!!savingHeating[kumes.KUMES_ID]}
                            sx={saveBtnSx}
                          >
                            {savingHeating[kumes.KUMES_ID] ? "…" : "Kaydet"}
                          </Button>
                        </Stack>

                        <Stack spacing={1.5}>
                          <TextField
                            fullWidth
                            select
                            size="small"
                            label="Yakıt Tipi"
                            value={currentHeating.YAKIT_TIP_ID ?? ""}
                            onChange={(e) =>
                              handleHeatingChange(
                                kumes.KUMES_ID,
                                "YAKIT_TIP_ID",
                                e.target.value
                              )
                            }
                            sx={fieldSx}
                          >
                            {yakitTipleri.map((t) => (
                              <MenuItem key={t.YAKIT_TIP_ID} value={t.YAKIT_TIP_ID}>
                                {t.YAKIT_TIP_ADI}
                              </MenuItem>
                            ))}
                          </TextField>

                          <TextField
                            fullWidth
                            select
                            size="small"
                            label="Soba Tipi"
                            value={currentHeating.SOBA_TIP_ID ?? ""}
                            onChange={(e) =>
                              handleHeatingChange(
                                kumes.KUMES_ID,
                                "SOBA_TIP_ID",
                                e.target.value
                              )
                            }
                            sx={fieldSx}
                          >
                            {sobaTipleri.map((t) => (
                              <MenuItem key={t.SOBA_TIP_ID} value={t.SOBA_TIP_ID}>
                                {t.SOBA_TIP_ADI}
                              </MenuItem>
                            ))}
                          </TextField>

                          <TextField
                            fullWidth
                            select
                            size="small"
                            label="Soba Konumu"
                            value={currentHeating.SOBA_YER_TIP_ID ?? ""}
                            onChange={(e) =>
                              handleHeatingChange(
                                kumes.KUMES_ID,
                                "SOBA_YER_TIP_ID",
                                e.target.value
                              )
                            }
                            sx={fieldSx}
                          >
                            {sobaKonumlari.map((t) => (
                              <MenuItem key={t.SOBA_YER_TIP_ID} value={t.SOBA_YER_TIP_ID}>
                                {t.SOBA_YER_TIP_ADI}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Stack>
                      </Box>
                    </Box>
                  </Paper>
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>
    </Box>
  );
};

export default KumesTeknikBilgilerContent;